﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import {
    buildReducerId,
    GUIDE_SUBSCRIPTION,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { useStyles } from "./Styles";
import type {
    ReducerContext,
    CustomerStartPageState,
    CustomerStartPageViewAction,
} from "./Store";
import { reducer } from "./Store";
import { useSettings } from "~base/Settings/SettingsContext";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import { useIntl } from "~base/Internationalization/IntlContext";
import { useGetSessionStartInfo } from "~base/Services/API/Session/GetStartInfo";
import type * as sessionModel from "~base/Services/API/Session/Model";
import { tryFormat } from "~base/Types/Identifier";
import { useNavigateService } from "~base/Services/NavigateService";
import type { CreateSessionEnsureSubjectRequest } from "~base/Services/API/Session/CreateSession";
import { useCreateSessionEnsureSubject } from "~base/Services/API/Session/CreateSession";
import { useEffect } from "react";
import {
    getExpectedSubjectType,
    getIdentifierTypesForModels,
    getSessionModelForSubjectTypes,
    sortSessionItemsDescending,
} from "~base/Pages/Home/Start/Functions";
import { getErrorMessage } from "~base/Services/API";
import { useGetSessions } from "~base/Services/API/Session/GetSessions";
import { useSearchParams } from "react-router-dom";
import { useResumeSession } from "~base/Services/API/Session/ResumeSession";
import { useServerContext } from "~base/Services/ServerContext";
import type { StartPageCustomizations } from "~base/Pages/Home/Start/Customization";
import { StartPageDefaultCustomization } from "~base/Pages/Home/Start/Customization";
import type {
    IdentifierConfigurationInfo,
    IndividualIdentifierTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import * as identifier from "~base/Types/Identifier";
import SoPNLStartPageCustomization from "../Start";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import { Ingress } from "~base/Components/Primitives/Ingress";
import { Button } from "~base/Components/Atoms/Button";
import StateSwitch from "~base/Components/Layout/StateSwitch";
import {
    CustomerStartPageSessionFilter,
    CustomerStartPageTab,
    NUM_PREV_SESSIONS_PER_PAGE,
} from "./Types";
import { Icon } from "~base/Components/Primitives/Icon";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import CustomerHeaderMenu from "~jrad/Pages/Shared/CustomerHeaderMenu";
import { Paginator } from "~base/Components/Composite/Paginator";
import classNames from "classnames";
import TitleSelect from "~base/Components/Primitives/TitleSelect";
import { SessionInteractionButtonText } from "./ChildComponents/SessionInteractionButtonText";
import TextBlock from "~base/Components/Primitives/TextBlock";
import { useGetUserCustomerDomain } from "~jrad/Services/API/Account/GetUserCustomerDomain";
import SearchButton from "~base/Pages/Home/Main/ChildComponents/Search";
import SoPNLMainPageCustomization from "~jrad/Pages/Home/Main/SoPNLMainPageCustomization";
import { useGuideSubscriptionService } from "~base/Utilities/GuideSubscriptionService";
import type { GuideDataBaseViewSpec } from "~base/Services/API/Steps/Model";

// SKIP-ApplyThemeStyleCode.ps1

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type CustomerStartPageProps = {};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const CustomerStartPage: React.FC<CustomerStartPageProps> = (
    props: CustomerStartPageProps
) => {
    // Init hooks

    const customization = {
        ...StartPageDefaultCustomization,
        ...SoPNLStartPageCustomization,
    };

    const intl = useIntl();
    const classes = useStyles();
    const navigate = useNavigateService();
    const settings = useSettings();
    const [searchParams] = useSearchParams();
    const serverContext = useServerContext();
    const userLanguage =
        serverContext.user?.PreferredUiCulture ??
        settings.valueAsString("account.defaultLanguage");

    const reducerContext: ReducerContext = {
        intl: intl,
    };

    const contextWrappedReducer = (reducerContext: ReducerContext) => {
        return (
            state: CustomerStartPageState,
            action: CustomerStartPageViewAction
        ) => reducer(state, action, reducerContext);
    };

    // Init state

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("CustomerStartPage"),
        contextWrappedReducer(reducerContext),
        {
            prevSessionPage: 1,
            prevSessionFilter: CustomerStartPageSessionFilter.All,
            selectedTab: CustomerStartPageTab.StartNewSession,
            subjectIdentifier: "",
            subjectIdentifierType: "",
            loading: true,
            matchingSubjectTypes: [],
        }
    );

    const [guideDispatch] = useGuideSubscriptionService();

    // Variables

    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const [individualTypes, organizationTypes] = getIdentifierTypesForModels(
        state.startInfo?.AllowedSubjectIdentifierTypes
    );

    const idConfig = getIdConfig(
        intl,
        customization,
        individualTypes,
        organizationTypes
    );

    const formattedSubjectIdentifier =
        state.matchingSubjectTypes?.length == 1
            ? tryFormat(intl, state.subjectIdentifier, idConfig)
            : state.subjectIdentifier;

    const sessionModels = getSessionModelForSubjectTypes(
        state.matchingSubjectTypes,
        state.startInfo?.SessionModels
    ).filter((sessionModel) => !sessionModel.SessionModelRestrictedWithMessage);

    const expectedSubjectType = getExpectedSubjectType(
        state.matchingSubjectTypes,
        sessionModels
    );

    const subjectIdentifierFqn = `${expectedSubjectType}|${formattedSubjectIdentifier}`;

    const userSessions =
        state.loadedSessionsForSubjectIdentifier ==
            formattedSubjectIdentifier && state.subjectSessions
            ? state.subjectSessions.sort(sortSessionItemsDescending)
            : new Array<sessionModel.Session>();

    const completeSessions = userSessions.filter((x) => x.state == "Completed");
    const ongoingSessions = userSessions.filter((x) => x.state == "Active");

    const lastSession = userSessions[0];

    // A model can have a customer-facing title that differs from the name advisors and admins see (e.g. without a variant suffix)
    const tryFormatCustomerTitle = (sessionModelFqn: string) =>
        intl.tryFormatText(sessionModelFqn + ".CustomerTitle") ??
        intl.tryFormatText(sessionModelFqn);
    const formatCustomerTitle = (sessionModelFqn: string) =>
        tryFormatCustomerTitle(sessionModelFqn) ??
        intl.formatText(sessionModelFqn);

    // control calling of hooks
    const callStartInfo =
        state.startInfo == null &&
        state.errorMessage == null &&
        !state.navigateUri;
    const calLoadSessions =
        state.errorMessage == null &&
        formattedSubjectIdentifier !=
            state.loadedSessionsForSubjectIdentifier &&
        state.matchingSubjectTypes?.length > 0 &&
        state.startInfo.SessionModels.length > 0;

    // Reset back state for Firefox.
    useEffect(() => {
        // When navigating back in firefox the previous page is displayed from memory.
        // This causes the page to be in "loading" state, since we were navigating to a new page
        // when the page was last displayed. We dispatch an action to reset the state.

        window.addEventListener("pageshow", () => {
            dispatch({ type: "pageRefresh" });
        });
    }, [dispatch]);

    // API hooks

    const [loadingStartInfo] = useGetSessionStartInfo({
        call: callStartInfo,
        salesOpportunity: null,
        throwError: false,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                if (responseData.RequiresRegistration) {
                    const encodedRelativeUrl = getReturnUrlParam();
                    dispatch({
                        type: "navigate",
                        uri: `/Account/Register?ReturnUrl=${encodedRelativeUrl}`,
                    });
                } else {
                    // Optionally get a subject identifier from QS params.
                    const subjectIdentifierParam =
                        searchParams.get("SubjectIdentifier");
                    const [subjectIdentifier, subjectIdentifierTypesFromQs] =
                        getMatchingSubjectTypes(
                            intl,
                            customization,
                            subjectIdentifierParam,
                            responseData
                        );

                    dispatch({
                        type: "initDataLoaded",
                        data: responseData,
                        subjectIdentifierFromQs: subjectIdentifier,
                        subjectIdentifierTypes: subjectIdentifierTypesFromQs,
                    });

                    if (responseData.SessionModels.length == 0) {
                        dispatch({
                            type: "fetchError",
                            message: intl.formatText(
                                "VisiFinance.UI.Start.StartaRådgivningSaknarSessionsmodeller"
                            ),
                        });
                    }
                }
            } else {
                if (response.status == 401) {
                    dispatch({
                        type: "navigate",
                        uri: `/Account/AccessDenied?ReturnUrl=${getReturnUrlParam()}`,
                    });
                } else {
                    // At this point the loading spinner is active, so we throw an error and show the generic dialog instead.
                    throw new Error(await getErrorMessage(intl, response));
                }
            }
        },
    });

    const [loadingGetSessions] = useGetSessions({
        call: calLoadSessions,
        subjectIdentifier: subjectIdentifierFqn,
        limit: 1000,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                const resDataFiltered = {
                    ...responseData,
                    items: responseData.items.filter(
                        customization.filerSessionsResume
                    ),
                };
                dispatch({
                    type: "sessionsForSubjectLoaded",
                    subjectidentifier: formattedSubjectIdentifier,
                    data: resDataFiltered,
                });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, response),
                });
            }
        },
    });

    const [loadingCreateSession, createSessionEnsureSubjectApi] =
        useCreateSessionEnsureSubject({
            callback: async (status, errorResponse, response) => {
                if (status == "Success") {
                    const sessionGuideUrl = `${serverVirtualPath}Home/Main/${response.id}`;
                    dispatch({ type: "navigate", uri: sessionGuideUrl });
                } else {
                    dispatch({
                        type: "fetchError",
                        message: await getErrorMessage(intl, errorResponse),
                    });
                }
            },
        });

    const [loadingResumeSession, resumeSessionApi] = useResumeSession({
        sessionId: -1,
        callback: async (status, errorResponse, response) => {
            if (status == "Success") {
                const sessionGuideUrl = `${serverVirtualPath}Home/Main/${response.sessionId}`;
                dispatch({ type: "navigate", uri: sessionGuideUrl });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, errorResponse),
                });
            }
        },
    });

    const [loadingUserCustomerDomain] = useGetUserCustomerDomain({
        call: !state.userCustomerDomain,
        callback: async (status, errorResponse, response) => {
            if (status == "Success") {
                dispatch({ type: "setUserCustomerDomain", value: response });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, errorResponse),
                });
            }
        },
    });

    // Change URL if a navigation has been dispatched.
    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(
                state.navigateUri,
                { replace: state.navigateReplace },
                state.navigateForceLoad
            );
        }
    }, [
        navigate,
        state.navigateUri,
        state.navigateReplace,
        state.navigateForceLoad,
    ]);

    const getPrevSessionsToShow = () => {
        switch (state.prevSessionFilter) {
            case CustomerStartPageSessionFilter.Complete: {
                return completeSessions;
            }
            case CustomerStartPageSessionFilter.Incomplete: {
                return ongoingSessions;
            }
            case CustomerStartPageSessionFilter.All:
            default: {
                return userSessions;
            }
        }
    };

    const pageLoading =
        !state.errorMessage &&
        (state.loading ||
            state.loadedSessionsForSubjectIdentifier == null ||
            loadingStartInfo ||
            loadingCreateSession ||
            loadingGetSessions ||
            loadingResumeSession ||
            loadingUserCustomerDomain ||
            !!state.navigateUri);

    const showPageHeader =
        !pageLoading ||
        loadingCreateSession ||
        loadingResumeSession ||
        !!state.navigateUri;

    const filteredSessions = getPrevSessionsToShow();

    const shownSessions = filteredSessions.slice(
        (state.prevSessionPage - 1) * NUM_PREV_SESSIONS_PER_PAGE,
        state.prevSessionPage * NUM_PREV_SESSIONS_PER_PAGE
    );

    const tenantName = serverContext.user?.Tenant.Name;
    const isAnonymousCustomer =
        serverContext.user.Roles.filter(
            (role) => role.Name == "customeranonymous"
        ).length > 0;
    const customerEmployerName =
        state.userCustomerDomain?.Properties.EmployerName;

    const providerName =
        customerEmployerName != null ? customerEmployerName : tenantName;

    const providerNamePensionFund =
        state.userCustomerDomain?.Properties.ProviderNamePensionFund ||
        tenantName;

    // Automatically start the session for anonymous customers if the domain only allows access to a single session model
    const willStartSessionForAnonymousCustomer =
        !pageLoading &&
        isAnonymousCustomer &&
        sessionModels.length == 1 &&
        userSessions.length == 0 &&
        !state.navigateUri;

    useEffect(() => {
        if (willStartSessionForAnonymousCustomer) {
            createSessionEnsureSubjectApi(
                getCreateSessionData(
                    state,
                    sessionModels[0].SessionModelId,
                    formattedSubjectIdentifier,
                    userLanguage
                )
            );
        }
    }, [
        createSessionEnsureSubjectApi,
        formattedSubjectIdentifier,
        sessionModels,
        state,
        userLanguage,
        willStartSessionForAnonymousCustomer,
    ]);

    useEffect(() => {
        if (!pageLoading) {
            // Delayed dispatch so the SearchOverlay can receive the subscription event correctly
            setTimeout(() => {
                guideDispatch("UpdateViewSpec", {
                    type: GUIDE_SUBSCRIPTION,
                    target: "UpdateViewSpec",
                    args: {
                        viewId: "CustomerStart",
                        subViewId: "CustomerStart",
                    } satisfies GuideDataBaseViewSpec,
                });
            }, 1000);
        }
    }, [pageLoading]);

    return (
        <ApplicationPage
            showHeader={showPageHeader}
            isLoading={pageLoading || willStartSessionForAnonymousCustomer}
            headerMenu={<CustomerHeaderMenu />}
            centerVertical={false}
            handleMargins={false}
            alignText="Left"
            contentWrapperClassName={classes.page}
            showFooter={false}
        >
            <div className={classes.hiddenSearchButton}>
                <SearchButton
                    localeOverride={
                        SoPNLMainPageCustomization.searchLocaleOverride
                    }
                />
            </div>
            <div className={classes.pageMenu}>
                <StateSwitch
                    dataTid="SelectedTab"
                    value={state.selectedTab}
                    textCodedValues={[
                        CustomerStartPageTab.StartNewSession,
                        CustomerStartPageTab.ResumeSessions,
                    ]}
                    onChange={(selectedTab) => {
                        dispatch({
                            type: "setSelectedTab",
                            value: selectedTab as CustomerStartPageTab,
                        });
                    }}
                />
            </div>
            <div className={classes.pageContainer}>
                <div
                    className={classNames(
                        state.selectedTab !==
                            CustomerStartPageTab.StartNewSession &&
                            classes.hiddenPageWidthSizer
                    )}
                >
                    <Rubrik
                        rubrikClassName={classes.pageHeader}
                        textCode={
                            isAnonymousCustomer
                                ? "SoPNL.UI.CustomerStartPage.Welcome.Anonymous.Header"
                                : "SoPNL.UI.CustomerStartPage.Welcome.Header"
                        }
                        textCodeArgs={[serverContext.user?.Name]}
                    />
                    <Ingress
                        ingressClassName={classes.pageIntroDescription}
                        styleType="Default"
                        htmlCode="SoPNL.UI.CustomerStartPage.Welcome.Description"
                        textCodeArgs={[providerName, tenantName]}
                    />
                </div>
                {state.errorMessage && (
                    <TextBlock
                        styleType="Paragraph"
                        text={state.errorMessage}
                        styleSubType="Validation"
                        size="Small"
                    />
                )}
                {state.selectedTab === CustomerStartPageTab.StartNewSession && (
                    <>
                        {lastSession && lastSession.state === "Active" && (
                            <div className={classes.resumeExistingSession}>
                                <div
                                    className={
                                        classes.resumeExistingSessionHeader
                                    }
                                >
                                    {intl.formatText(
                                        "SoPNL.UI.CustomerStartPage.ResumeLastSession.Header"
                                    )}
                                </div>
                                <div
                                    className={
                                        classes.resumeExistingSessionContainer
                                    }
                                >
                                    <div
                                        className={
                                            classes.resumeExistingSessionDescription
                                        }
                                    >
                                        {intl.formatText(
                                            "SoPNL.UI.CustomerStartPage.ResumeLastSession.Description",
                                            formatCustomerTitle(
                                                lastSession.sessionModelFqn
                                            ),
                                            intl.formatDateMonthLong(
                                                lastSession.creationDate
                                            )
                                        )}
                                    </div>
                                    <Button
                                        testIdentifier="ResumeLastSession"
                                        disabled={!!pageLoading}
                                        onClick={() => {
                                            resumeSessionApi(lastSession.id);
                                        }}
                                        className={
                                            classes.resumeExistingSessionButton
                                        }
                                    >
                                        {intl.formatText(
                                            "SoPNL.UI.CustomerStartPage.Session.Action.Resume"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className={classes.sessionModelsHeader}>
                            {intl.formatText(
                                "SoPNL.UI.CustomerStartPage.StartNewSession.Header"
                            )}
                        </div>

                        <div className={classes.sessionModelsGrid}>
                            {sessionModels.map((sessionModel) => (
                                <div
                                    key={sessionModel.SessionModelId}
                                    className={classes.sessionModelContainer}
                                >
                                    <Icon
                                        svgClassName={classes.sessionModelIcon}
                                        iconFQN="Consilio.UI.Icons.Chart"
                                    />
                                    <div
                                        className={
                                            classes.sessionModelContentContainer
                                        }
                                    >
                                        <div
                                            className={
                                                classes.sessionModelHeader
                                            }
                                        >
                                            {formatCustomerTitle(
                                                sessionModel.SessionModelFqn
                                            )}
                                        </div>
                                        <div
                                            className={
                                                classes.sessionModelDescription
                                            }
                                        >
                                            {intl.formatText(
                                                sessionModel.SessionModelFqn +
                                                    ".Description",
                                                tenantName,
                                                providerNamePensionFund
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        testIdentifier={`StartSession.${sessionModel.SessionModelId}`}
                                        disabled={!!pageLoading}
                                        onClick={() => {
                                            createSessionEnsureSubjectApi(
                                                getCreateSessionData(
                                                    state,
                                                    sessionModel.SessionModelId,
                                                    formattedSubjectIdentifier,
                                                    userLanguage
                                                )
                                            );
                                        }}
                                        className={classes.sessionModelButton}
                                    >
                                        {intl.formatText(
                                            "SoPNL.UI.CustomerStartPage.Session.Action.Start"
                                        )}
                                    </Button>
                                    {!!sessionModel.SessionModelRestrictedWithMessage && (
                                        <div
                                            className={
                                                classes.sessionModelBlockedOverlay
                                            }
                                        >
                                            <div
                                                className={
                                                    classes.sessionModelBlockedOverlayContent
                                                }
                                            >
                                                {intl.formatText(
                                                    sessionModel.SessionModelRestrictedWithMessage
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {state.selectedTab === CustomerStartPageTab.ResumeSessions && (
                    <>
                        <div className={classes.sessionModelsHeader}>
                            {intl.formatText(
                                "SoPNL.UI.CustomerStartPage.PrevSessions.Header"
                            )}
                        </div>

                        {userSessions.length === 0 ? (
                            <TextBlock
                                styleType="Paragraph"
                                textCode="SoPNL.UI.CustomerStartPage.PrevSessions.EmptyDescription"
                                size="Small"
                            />
                        ) : (
                            <>
                                {ongoingSessions.length > 0 &&
                                    completeSessions.length > 0 && (
                                        <TitleSelect
                                            testIdentifier="SoPNL.UI.CustomerStartPage.PrevSessions.Filter.Header"
                                            required
                                            titleTextCode="SoPNL.UI.CustomerStartPage.PrevSessions.Filter.Header"
                                            textCodedValues={[
                                                {
                                                    displayName:
                                                        "SoPNL.UI.CustomerStartPage.PrevSessions.Filter.Option.All",
                                                    value: CustomerStartPageSessionFilter.All,
                                                },
                                                {
                                                    displayName:
                                                        "SoPNL.UI.CustomerStartPage.PrevSessions.Filter.Option.Incomplete",
                                                    value: CustomerStartPageSessionFilter.Incomplete,
                                                },
                                                {
                                                    displayName:
                                                        "SoPNL.UI.CustomerStartPage.PrevSessions.Filter.Option.Complete",
                                                    value: CustomerStartPageSessionFilter.Complete,
                                                },
                                            ]}
                                            value={state.prevSessionFilter}
                                            onChange={(value) => {
                                                dispatch({
                                                    type: "setPrevSessionFilter",
                                                    value,
                                                });
                                            }}
                                            padding={[]}
                                        />
                                    )}

                                <Paginator
                                    testIdentifier="Paginator"
                                    totalPages={Math.ceil(
                                        filteredSessions.length /
                                            NUM_PREV_SESSIONS_PER_PAGE
                                    )}
                                    currentPage={state.prevSessionPage}
                                    setCurrentPage={(page) => {
                                        dispatch({
                                            type: "setPrevSessionPage",
                                            page,
                                        });
                                    }}
                                    showArrows
                                    minEdgePagesToShow={1}
                                    maxPagesToShow={15}
                                >
                                    <div className={classes.sessionModelsGrid}>
                                        {shownSessions.map((session) => (
                                            <div
                                                key={session.id}
                                                className={
                                                    classes.sessionModelContainer
                                                }
                                            >
                                                <div
                                                    className={
                                                        classes.sessionModelContentContainer
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.sessionModelHeader
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                classes.sessionHeaderDate
                                                            }
                                                        >
                                                            {intl.formatDateMonthTimeLong(
                                                                session.creationDate
                                                            )}
                                                        </span>
                                                        <span
                                                            className={
                                                                classes.sessionHeaderStatus
                                                            }
                                                        >
                                                            {intl.formatText(
                                                                session.state ===
                                                                    "Completed"
                                                                    ? "SoPNL.UI.CustomerStartPage.Session.Status.Complete"
                                                                    : "SoPNL.UI.CustomerStartPage.Session.Status.Incomplete"
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={
                                                            classes.sessionModelDescription
                                                        }
                                                    >
                                                        {tryFormatCustomerTitle(
                                                            session.sessionModelFqn
                                                        ) ?? ""}
                                                    </div>
                                                </div>
                                                <Button
                                                    testIdentifier={`ResumeSession.${session.id}`}
                                                    disabled={!!pageLoading}
                                                    onClick={() => {
                                                        resumeSessionApi(
                                                            session.id
                                                        );
                                                    }}
                                                    className={
                                                        classes.sessionModelButton
                                                    }
                                                >
                                                    <SessionInteractionButtonText
                                                        textCode={
                                                            session.state ===
                                                            "Completed"
                                                                ? "SoPNL.UI.CustomerStartPage.Session.Action.View"
                                                                : "SoPNL.UI.CustomerStartPage.Session.Action.Resume"
                                                        }
                                                    />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </Paginator>
                            </>
                        )}
                    </>
                )}
            </div>
        </ApplicationPage>
    );
};

const getReturnUrlParam = () => {
    const relativeUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
    const encodedRelativeUrl = encodeURIComponent(relativeUrl);

    return encodedRelativeUrl;
};

const getMatchingSubjectTypes = (
    intl: IntlContext,
    customization: StartPageCustomizations,
    subjectIdentifierParam: string,
    startInfo: sessionModel.GetStartInfoResponse
): [string, identifier.IdentifierParseTypes] => {
    if (!subjectIdentifierParam) {
        return [null, null];
    }

    const [individualTypes, organizationTypes] = getIdentifierTypesForModels(
        startInfo?.AllowedSubjectIdentifierTypes
    );
    const idConfig = getIdConfig(
        intl,
        customization,
        individualTypes,
        organizationTypes
    );
    const builtInResult = identifier.validate(
        intl,
        subjectIdentifierParam,
        idConfig
    );

    const formattedSubjectIdentifier =
        builtInResult.parsedValue.matchingTypes?.length == 1
            ? tryFormat(intl, subjectIdentifierParam, idConfig)
            : subjectIdentifierParam;

    return [formattedSubjectIdentifier, builtInResult.parsedValue];
};

const getIdConfig = (
    intl: IntlContext,
    customization: StartPageCustomizations,
    individualIdTypes: IndividualIdentifierTypes[],
    orgIdTypes: OrganizationIdentifierTypes[]
): IdentifierConfigurationInfo => {
    return {
        ...intl.configInfo.identifier,
        individualFormat:
            customization.individualIdentifierFormat ??
            intl.configInfo.identifier.individualFormat,
        organizationFormat:
            customization.organizationIdentifierFormat ??
            intl.configInfo.identifier.organizationFormat,
        individualTypes: individualIdTypes,
        organizationTypes: orgIdTypes,
        organizationSubTypes: [
            ...intl.configInfo.identifier.organizationSubTypes,
            ...customization.additionalAllowedOrganizationTypes,
        ],
    };
};

const getCreateSessionData = (
    state: CustomerStartPageState,
    sessionTemplateId: number,
    formattedSubjectIdentifier: string,
    locale: string
): CreateSessionEnsureSubjectRequest => {
    let subjectType = "";
    switch (state.subjectIdentifierType) {
        case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Personnummer.SWE":
            subjectType =
                "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson";
            break;
        case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson.Epost":
            subjectType =
                "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.FysiskPerson";
            break;
        case "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson.Organisationsnummer.SWE":
            subjectType =
                "VisiFinance.Consilio.Rådgivningssubjekt.Identifierare.JuridiskPerson";
            break;
    }

    return {
        locale: locale,
        sessionTemplateId: sessionTemplateId,
        subjectIdentifier: formattedSubjectIdentifier,
        subjectType: subjectType,
        subjectIdentifierType: state.subjectIdentifierType,
        externalSessionIdentifier: null,
        initialInfRules: null,
        salesOpportunity: null,
    };
};
